import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getTotalPrizeDetail, TotalPrizeDetailResponse } from "@/Api/totalPrizes";
import { Mask } from "@/utils/mask";
import { formatDateBr, lotteryColor } from "@/pages/ClientResults/shared";

interface DetailModalProps {
  contestId: number | null;
  onClose: () => void;
}

/** Bolões premiados de um concurso (somente leitura). */
export const DetailModal = ({ contestId, onClose }: DetailModalProps) => {
  const [detail, setDetail] = useState<TotalPrizeDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contestId) {
      setDetail(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data, ok } = await getTotalPrizeDetail(contestId);
        setDetail(ok && data?.response ? data.response : null);
      } finally {
        setLoading(false);
      }
    })();
  }, [contestId]);

  const contest = detail?.contest;

  return (
    <Dialog open={!!contestId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {contest ? (
              <span>
                <span style={{ color: lotteryColor(contest.lottery_color) }}>{contest.lottery_name}</span> | Concurso{" "}
                {contest.contest_name}
              </span>
            ) : (
              "Premiações do concurso"
            )}
          </DialogTitle>
          {contest && (
            <DialogDescription>
              Data de validade: {formatDateBr(contest.validation_date)}
              {!contest.is_active && " · concurso desativado"}
              {contest.investigation_status === "partial" &&
                ` · apuração parcial (${contest.investigated_products} de ${contest.total_products} bolões lançados)`}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !detail ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Não foi possível carregar o concurso.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-left font-normal">Bolão</th>
                  <th className="px-3 py-2 text-right font-normal">Prêmio por cota</th>
                  <th className="px-3 py-2 text-right font-normal">Premiação total</th>
                </tr>
              </thead>
              <tbody>
                {detail.products.map((p) => (
                  <tr key={p.contest_product_id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">Bolão {p.product_name}</span>
                        {p.receive_by_cashier && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            Recebido no caixa
                          </span>
                        )}
                      </div>
                      {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{Mask.formatCurrency(p.prize_per_quota)}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-green-700 dark:text-green-400">
                      {Mask.formatCurrency(p.prize_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-green-50 font-semibold dark:bg-green-900/10">
                  <td className="px-3 py-2">
                    {detail.summary.awarded_tickets}{" "}
                    {detail.summary.awarded_tickets === 1 ? "bilhete premiado" : "bilhetes premiados"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {Mask.formatCurrency(detail.summary.prize_per_quota_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-green-700 dark:text-green-400">
                    {Mask.formatCurrency(detail.summary.prize_total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
